import { useEffect, useMemo, useState } from 'react';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { ErrorMessage } from '@/components/ui/error-message';
import { Spinner } from '@/components/ui/spinner';
import { useRegisterListQuery, type RegisterEntry } from '@/lib/api/registers';
import { useCreateExcelAnnotationMutation } from '@/lib/api/excel-annotations';
import type { ExcelCellSelection } from './excel-workbook';

/**
 * T-0036 / E04-F05 — cell-annotation modal.
 *
 * Inline section reveal (NOT a Radix Dialog) per the project's v1
 * convention — `apps/web/src/components/ui/` doesn't expose a Dialog
 * component yet. Matches the proposals tab's `verbModal` pattern.
 *
 * The annotation IS an EvidenceLink (ADR-014 + E04-F03 §3). The modal
 * picks a register entry as the source and posts the link via
 * `useCreateExcelAnnotationMutation` (which under the hood calls the
 * T-0034 evidence-links POST endpoint with target_kind='excel_cell').
 *
 * v1 source picker: contract-compliance baseline register only. The
 * full picker (register-type selector + filtered entry list) is a
 * follow-up; this surface is the minimum bar to land the annotation
 * flow end-to-end.
 */

const DEFAULT_REGISTER_TYPE = 'contract_compliance_baseline';

export interface CellAnnotationModalProps {
  engagementId: string;
  documentId: string;
  selection: ExcelCellSelection;
  /** Called on success after the annotation lands. */
  onSuccess?: () => void;
  /** Called when the user cancels or closes the modal. */
  onCancel: () => void;
}

export function CellAnnotationModal({
  engagementId,
  documentId,
  selection,
  onSuccess,
  onCancel,
}: CellAnnotationModalProps) {
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<unknown>(null);

  // Idempotency-Key derived from the (engagement, document, sheet, cell,
  // source) tuple so a retry from a flaky network surfaces the same
  // EvidenceLink id back. Format mirrors the cross-cutting standards §2
  // suggestion: "<scope>:<resource-shape>:<unique-bits>".
  const idempotencyKey = useMemo(() => {
    if (!selectedEntryId) return undefined;
    return `excel-annot:${engagementId}:${documentId}:${selection.sheet_name}:${selection.cell_a1}:${selectedEntryId}`;
  }, [engagementId, documentId, selection.sheet_name, selection.cell_a1, selectedEntryId]);

  const registers = useRegisterListQuery(engagementId, DEFAULT_REGISTER_TYPE, { limit: 25 });
  const mutation = useCreateExcelAnnotationMutation(
    engagementId,
    documentId,
    selection.sheet_name,
    selection.cell_a1,
  );

  // Reset the picker when the selection target changes so a previous
  // pick doesn't carry forward to a new cell.
  useEffect(() => {
    setSelectedEntryId(null);
    setSubmitError(null);
  }, [selection.sheet_name, selection.cell_a1]);

  const submit = () => {
    if (!selectedEntryId) return;
    setSubmitError(null);
    mutation.mutate(
      {
        source_register_entry_id: selectedEntryId,
        ...(idempotencyKey ? { idempotencyKey } : {}),
      },
      {
        onSuccess: () => {
          onSuccess?.();
        },
        onError: (err) => {
          setSubmitError(err);
        },
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Annotate cell</CardTitle>
        <CardDescription>
          Cite this cell as evidence on a register entry. The annotation persists as an EvidenceLink
          with <span className="font-mono">target_kind='excel_cell'</span>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <Label>Sheet</Label>
            <p className="font-mono text-sm">{selection.sheet_name}</p>
          </div>
          <div>
            <Label>Cell</Label>
            <p className="font-mono text-sm">{selection.cell_a1}</p>
          </div>
          <div>
            <Label>Snapshot</Label>
            <p className="truncate font-mono text-sm" title={selection.value_snapshot ?? ''}>
              {selection.value_snapshot ?? <span className="text-muted-foreground">empty</span>}
            </p>
          </div>
        </div>

        <div>
          <Label>Cite from register entry</Label>
          <p className="mb-2 text-xs text-muted-foreground">
            v1 picker — contract-compliance baseline only. A future iteration adds the full
            register-type selector.
          </p>
          {registers.isLoading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Spinner /> Loading register entries…
            </div>
          ) : registers.error ? (
            <ErrorMessage error={registers.error} />
          ) : !registers.data || registers.data.entries.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No register entries in this engagement. Seed the register before annotating.
            </p>
          ) : (
            <ul className="max-h-48 divide-y overflow-y-auto rounded-md border">
              {registers.data.entries.map((entry) => (
                <EntryRow
                  key={entry.id}
                  entry={entry}
                  selected={selectedEntryId === entry.id}
                  onSelect={() => setSelectedEntryId(entry.id)}
                />
              ))}
            </ul>
          )}
        </div>

        {submitError ? <ErrorMessage error={submitError} /> : null}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={mutation.isPending}>
            Cancel
          </Button>
          <Button onClick={submit} disabled={!selectedEntryId || mutation.isPending}>
            {mutation.isPending ? <Spinner /> : null}
            <span>Annotate this cell</span>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function EntryRow({
  entry,
  selected,
  onSelect,
}: {
  entry: RegisterEntry;
  selected: boolean;
  onSelect: () => void;
}) {
  // v1 label is the entry id slice + status — the payload shape is
  // register-type-specific and a richer card would need per-type
  // formatting. Sufficient for the v1 annotation flow.
  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        className={
          'flex w-full items-center justify-between gap-3 p-2 text-left text-sm hover:bg-muted/50 ' +
          (selected ? 'bg-muted/60 font-medium' : '')
        }
      >
        <span className="font-mono text-xs">{entry.id.slice(0, 8)}…</span>
        <span className="text-xs text-muted-foreground">{entry.status}</span>
      </button>
    </li>
  );
}
